import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { app, db, getAuthInstance } from '@/lib/firebase';
import { collection, getDocs, query, orderBy, doc, updateDoc } from 'firebase/firestore';
import { usePaulAccess } from '@/lib/paulAccess';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// The miner functions are deployed in this app's own Firebase project.
const functionsOrigin = (): string => {
  const projectId =
    (app as { options?: { projectId?: string } } | undefined)?.options?.projectId ||
    (import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined) ||
    'studio-8849194767-f8721';
  return `https://us-central1-${projectId}.cloudfunctions.net`;
};

// Exec-gated call: attach the current user's Firebase ID token as a bearer.
async function callFn<T = unknown>(name: string, body?: unknown): Promise<T> {
  const auth = await getAuthInstance();
  const user = auth?.currentUser;
  if (!user) throw new Error('You must be signed in as an exec.');
  const token = await user.getIdToken();
  const res = await fetch(`${functionsOrigin()}/${name}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body ?? {}),
  });
  const text = await res.text();
  let json: unknown;
  try { json = JSON.parse(text); } catch { json = { raw: text }; }
  if (!res.ok) {
    const msg = (json as { error?: string })?.error || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return json as T;
}

type Channel = {
  channelId: string;
  title?: string;
  handle?: string | null;
  status?: string;
  videoCount?: number;
  addedBy?: string;
  color?: string;
};

type Video = {
  videoId: string;
  title?: string;
  channelTitle?: string;
  publishDateKey?: string;
  publishedAt?: string;
  durationSec?: number;
  viewCount?: number;
  transcriptStatus?: string;
  transcriptLang?: string;
  extractionStatus?: string;
  eventCount?: number;
  url?: string;
  thumbnailUrl?: string;
  description?: string;
};

type EventRow = {
  id: string;
  eventDateKey?: string;
  datePrecision?: string;
  confidence?: number;
  title?: string;
  summary?: string;
  channelTitle?: string;
  channelId?: string;
  videoId?: string;
  sourceQuote?: string;
  sourceTimestampSec?: number;
  sourceTimestampUrl?: string;
  reviewStatus?: string;
  model?: string;
  topics?: string[];
  extractedAt?: { seconds?: number } | string;
};

const num = (v: unknown): number | undefined => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v.trim() !== '') return Number(v);
  return undefined;
};
const fmtNum = (n?: number) => (typeof n === 'number' ? n.toLocaleString() : '—');
const fmtDur = (s?: number) => {
  if (!s || s < 0) return '—';
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
};
const confVariant = (c?: number) => ((c ?? 0) >= 0.85 ? 'default' : (c ?? 0) >= 0.6 ? 'secondary' : 'outline');
const extractedSecs = (e: EventRow): number => {
  const x = e.extractedAt;
  if (x && typeof x === 'object' && typeof x.seconds === 'number') return x.seconds;
  if (typeof x === 'string') { const t = Date.parse(x); return Number.isFinite(t) ? t / 1000 : 0; }
  return 0;
};

export default function TimelineMiner() {
  const { email } = usePaulAccess();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [showHidden, setShowHidden] = useState(false);
  const [videos, setVideos] = useState<Video[]>([]);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [channelInput, setChannelInput] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [status, setStatus] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  const [tab, setTab] = useState<'events' | 'videos' | 'ask' | 'billing'>('events');
  // Usage & per-tenant API keys (billing).
  const [keys, setKeys] = useState<Array<{ keyId: string; tenant: string; label?: string | null; active?: boolean }>>([]);
  const [usage, setUsage] = useState<{ rollups: Array<Record<string, any>>; totalCostUsd?: number; totalTokens?: number } | null>(null);
  const [newTenant, setNewTenant] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [createdKey, setCreatedKey] = useState<{ tenant: string; key: string } | null>(null);
  // RAG chat (Ask AI).
  const [askQuestion, setAskQuestion] = useState('');
  const [askAnswer, setAskAnswer] = useState<string | null>(null);
  const [askCitations, setAskCitations] = useState<(EventRow & { n?: number; score?: number })[]>([]);
  const [askBusy, setAskBusy] = useState(false);
  // filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [precision, setPrecision] = useState('all');
  const [minConf, setMinConf] = useState(0);
  const [sortBy, setSortBy] = useState<'extractedAt' | 'eventDate' | 'confidence'>('extractedAt');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [visibleCount, setVisibleCount] = useState(50);
  // AI semantic search (server-side embeddings retrieval).
  const [aiMode, setAiMode] = useState(false);
  const [aiResults, setAiResults] = useState<(EventRow & { score?: number })[] | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Event review session: a snapshot of filtered event ids so Prev/Next/Confirm stay stable
  // even as review status changes underneath. reviewPos indexes into reviewQueue.
  const [reviewQueue, setReviewQueue] = useState<string[] | null>(null);
  const [reviewPos, setReviewPos] = useState(0);
  // Plain in-app video playback (Videos tab), no event context.
  const [videoPlayer, setVideoPlayer] = useState<{ videoId: string; title?: string } | null>(null);

  const loadChannels = useCallback(async () => {
    if (!db) {
      setStatus({ kind: 'err', msg: 'Firestore is disabled on this browser (db is null). Run localStorage.removeItem("disableFirestore") then reload.' });
      return;
    }
    const d = db;
    try {
      const snap = await getDocs(query(collection(d, 'youtubeChannels'), orderBy('title')))
        .catch(() => getDocs(collection(d, 'youtubeChannels')));
      setChannels(snap.docs.map((x) => ({ channelId: x.id, ...(x.data() as object) }) as Channel));
    } catch (e) {
      setStatus({ kind: 'err', msg: `Failed to load channels: ${e instanceof Error ? e.message : String(e)}` });
    }
  }, []);

  const loadVideos = useCallback(async () => {
    if (!db) return;
    const d = db;
    try {
      const snap = await getDocs(query(collection(d, 'youtubeVideos'), orderBy('publishedAt', 'desc')))
        .catch(() => getDocs(collection(d, 'youtubeVideos')));
      setVideos(snap.docs.map((x) => {
        const data = x.data() as Record<string, unknown>;
        return {
          videoId: x.id,
          ...data,
          durationSec: num(data.durationSec),
          viewCount: num(data.viewCount),
          eventCount: num(data.eventCount),
        } as Video;
      }));
    } catch (e) {
      setStatus({ kind: 'err', msg: `Failed to load videos: ${e instanceof Error ? e.message : String(e)}` });
    }
  }, []);

  const loadEvents = useCallback(async () => {
    if (!db) return;
    const d = db;
    try {
      // Load the full set (846-ish) and filter/sort client-side.
      const snap = await getDocs(query(collection(d, 'timelineEvents'), orderBy('extractedAt', 'desc')))
        .catch(() => getDocs(collection(d, 'timelineEvents')));
      setEvents(snap.docs.map((x) => {
        const data = x.data() as Record<string, unknown>;
        return { id: x.id, ...data, confidence: num(data.confidence) } as EventRow;
      }));
    } catch (e) {
      setStatus({ kind: 'err', msg: `Failed to load events: ${e instanceof Error ? e.message : String(e)}` });
    }
  }, []);

  const reloadAll = useCallback(async () => {
    await Promise.all([loadChannels(), loadVideos(), loadEvents()]);
  }, [loadChannels, loadVideos, loadEvents]);

  useEffect(() => { void reloadAll(); }, [reloadAll]);

  const run = async (key: string, fn: () => Promise<string>) => {
    setBusy(key);
    setStatus(null);
    try {
      const msg = await fn();
      setStatus({ kind: 'ok', msg });
      await reloadAll();
    } catch (e) {
      setStatus({ kind: 'err', msg: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(null);
    }
  };

  const register = () =>
    run('register', async () => {
      const input = channelInput.trim();
      if (!input) throw new Error('Enter a channel URL, @handle, or ID.');
      const r = await callFn<{ channel?: { title?: string }; backfill?: { videosWritten?: number } }>(
        'registerChannel', { channelInput: input },
      );
      setChannelInput('');
      return `Registered “${r.channel?.title ?? input}” · backfilled ${r.backfill?.videosWritten ?? 0} videos.`;
    });

  const setChannelHidden = (c: Channel, hidden: boolean) =>
    run(`hide:${c.channelId}`, async () => {
      if (!db) throw new Error('Firestore is disabled on this browser.');
      await updateDoc(doc(db, 'youtubeChannels', c.channelId), { status: hidden ? 'hidden' : 'active' });
      return `${hidden ? 'Hid' : 'Restored'} “${c.title ?? c.channelId}”.`;
    });

  // Per-channel event colour (shown on the calendar bars + chips). Optimistic, no full reload.
  const setChannelColor = async (c: Channel, color: string) => {
    if (!db) return;
    setChannels((prev) => prev.map((x) => (x.channelId === c.channelId ? { ...x, color } : x)));
    try {
      await updateDoc(doc(db, 'youtubeChannels', c.channelId), { color });
    } catch (e) {
      setStatus({ kind: 'err', msg: `Color save failed: ${e instanceof Error ? e.message : String(e)}` });
    }
  };

  const backfill = (c: Channel) =>
    run(`backfill:${c.channelId}`, async () => {
      const r = await callFn<{ videosWritten?: number }>('backfillChannel', { channelId: c.channelId });
      return `Backfilled “${c.title}” · ${r.videosWritten ?? 0} new videos.`;
    });

  const extract = (c: Channel, limit: number) =>
    run(`extract:${c.channelId}`, async () => {
      const r = await callFn<{ processed?: number; eventsCreated?: number }>('extractPending', {
        channelId: c.channelId, limit,
      });
      return `Extracted ${r.processed ?? 0} video(s) from “${c.title}” · ${r.eventsCreated ?? 0} new events.`;
    });

  const review = (id: string, reviewStatus: 'confirmed' | 'rejected') =>
    run(`review:${id}`, async () => {
      await callFn('reviewEvent', { eventId: id, reviewStatus });
      return `Marked event ${reviewStatus}.`;
    });

  const bulkReview = (ids: string[], reviewStatus: 'confirmed' | 'rejected') =>
    run(`bulk:${reviewStatus}`, async () => {
      let ok = 0;
      for (const id of ids) {
        try { await callFn('reviewEvent', { eventId: id, reviewStatus }); ok += 1; } catch { /* continue */ }
      }
      setSelected(new Set());
      return `${reviewStatus === 'confirmed' ? 'Confirmed' : 'Rejected'} ${ok}/${ids.length} events.`;
    });

  // ---- filtering + sorting (client-side over the full set) ----
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = events.filter((e) => {
      if (statusFilter !== 'all' && (e.reviewStatus ?? 'unreviewed') !== statusFilter) return false;
      if (precision !== 'all' && e.datePrecision !== precision) return false;
      if ((e.confidence ?? 0) < minConf) return false;
      if (q) {
        const hay = `${e.title ?? ''} ${e.summary ?? ''} ${e.channelTitle ?? ''} ${(e.topics ?? []).join(' ')} ${e.sourceQuote ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const dir = sortDir === 'desc' ? -1 : 1;
    list = [...list].sort((a, b) => {
      if (sortBy === 'confidence') return ((a.confidence ?? 0) - (b.confidence ?? 0)) * dir;
      if (sortBy === 'eventDate') return String(a.eventDateKey ?? '').localeCompare(String(b.eventDateKey ?? '')) * dir;
      return (extractedSecs(a) - extractedSecs(b)) * dir;
    });
    return list;
  }, [events, search, statusFilter, precision, minConf, sortBy, sortDir]);

  const shown = filtered.slice(0, visibleCount);
  // The list actually displayed: AI results when in semantic mode, else the keyword-filtered page.
  const rows = aiMode ? (aiResults ?? []) : shown;
  useEffect(() => { setVisibleCount(50); }, [search, statusFilter, precision, minConf, sortBy, sortDir]);

  const runAiSearch = useCallback(async () => {
    const q = search.trim();
    if (!q) { setAiResults(null); return; }
    setAiBusy(true); setStatus(null);
    try {
      const r = await callFn<{ results: (EventRow & { score?: number })[] }>('searchTimeline', {
        query: q, topK: 50,
        reviewStatus: statusFilter !== 'all' ? statusFilter : undefined,
        minConfidence: minConf || undefined,
        datePrecision: precision !== 'all' ? precision : undefined,
      });
      setAiResults(r.results || []);
    } catch (e) {
      setStatus({ kind: 'err', msg: `AI search failed: ${e instanceof Error ? e.message : String(e)}` });
      setAiResults([]);
    } finally { setAiBusy(false); }
  }, [search, statusFilter, minConf, precision]);

  const counts = useMemo(() => {
    const c = { unreviewed: 0, confirmed: 0, rejected: 0 };
    for (const e of events) {
      const s = (e.reviewStatus ?? 'unreviewed') as keyof typeof c;
      if (s in c) c[s] += 1;
    }
    return c;
  }, [events]);

  const allShownSelected = rows.length > 0 && rows.every((e) => selected.has(e.id));
  const toggleSel = (id: string) => setSelected((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const selectAllShown = () => setSelected((prev) => {
    const n = new Set(prev);
    if (allShownSelected) rows.forEach((e) => n.delete(e.id));
    else rows.forEach((e) => n.add(e.id));
    return n;
  });

  // ---- event review session (player dialog) ----
  const eventsById = useMemo(() => new Map(events.map((e) => [e.id, e])), [events]);
  const reviewEvent = reviewQueue ? eventsById.get(reviewQueue[reviewPos]) ?? null : null;
  const reviewLen = reviewQueue?.length ?? 0;

  const openReview = useCallback((eventId: string, source?: { id: string }[]) => {
    const src = source ?? (aiMode ? (aiResults ?? []) : filtered);
    const ids = src.map((x) => x.id);
    const idx = ids.indexOf(eventId);
    setReviewQueue(ids);
    setReviewPos(idx >= 0 ? idx : 0);
  }, [aiMode, aiResults, filtered]);

  const loadBilling = useCallback(async () => {
    try {
      const [k, u] = await Promise.all([
        callFn<{ keys: typeof keys }>('manageAiKeys', { action: 'list' }),
        callFn<{ rollups: Array<Record<string, any>>; totalCostUsd: number; totalTokens: number }>('aiUsageReport', {}),
      ]);
      setKeys(k.keys || []);
      setUsage(u);
    } catch (e) {
      setStatus({ kind: 'err', msg: `Billing load failed: ${e instanceof Error ? e.message : String(e)}` });
    }
  }, []);

  const createKey = useCallback(async () => {
    const t = newTenant.trim();
    if (!t) return;
    setBusy('createkey'); setStatus(null); setCreatedKey(null);
    try {
      const r = await callFn<{ key: string; tenant: string }>('manageAiKeys', { action: 'create', tenant: t, label: newLabel.trim() || undefined });
      setCreatedKey({ tenant: r.tenant, key: r.key });
      setNewTenant(''); setNewLabel('');
      await loadBilling();
    } catch (e) {
      setStatus({ kind: 'err', msg: `Create key failed: ${e instanceof Error ? e.message : String(e)}` });
    } finally { setBusy(null); }
  }, [newTenant, newLabel, loadBilling]);

  const revokeKey = useCallback(async (keyId: string) => {
    setBusy(`revoke:${keyId}`); setStatus(null);
    try { await callFn('manageAiKeys', { action: 'revoke', keyId }); await loadBilling(); }
    catch (e) { setStatus({ kind: 'err', msg: `Revoke failed: ${e instanceof Error ? e.message : String(e)}` }); }
    finally { setBusy(null); }
  }, [loadBilling]);

  useEffect(() => { if (tab === 'billing') void loadBilling(); }, [tab, loadBilling]);

  const runAsk = useCallback(async () => {
    const q = askQuestion.trim();
    if (!q) return;
    setAskBusy(true); setAskAnswer(null); setStatus(null);
    try {
      const r = await callFn<{ answer: string; citations: (EventRow & { n?: number })[] }>('askTimeline', { question: q, topK: 12 });
      setAskAnswer(r.answer || '(no answer returned)');
      setAskCitations(r.citations || []);
    } catch (e) {
      setStatus({ kind: 'err', msg: `Ask failed: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setAskBusy(false);
    }
  }, [askQuestion]);

  const goReview = useCallback((delta: number) => {
    setReviewPos((p) => {
      const len = reviewQueue?.length ?? 0;
      if (!len) return p;
      return Math.max(0, Math.min(len - 1, p + delta));
    });
  }, [reviewQueue]);

  const reviewCurrent = useCallback(async (reviewStatus: 'confirmed' | 'rejected') => {
    const id = reviewQueue?.[reviewPos];
    if (!id) return;
    setEvents((prev) => prev.map((e) => (e.id === id ? { ...e, reviewStatus } : e))); // optimistic
    setAiResults((prev) => (prev ? prev.map((e) => (e.id === id ? { ...e, reviewStatus } : e)) : prev));
    goReview(1); // auto-advance
    try { await callFn('reviewEvent', { eventId: id, reviewStatus }); }
    catch (e) { setStatus({ kind: 'err', msg: `Review failed: ${e instanceof Error ? e.message : String(e)}` }); }
  }, [reviewQueue, reviewPos, goReview]);

  useEffect(() => {
    if (!reviewQueue) return;
    const onKey = (ev: KeyboardEvent) => {
      const t = ev.target as HTMLElement | null;
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      if (ev.key === 'ArrowRight') { ev.preventDefault(); goReview(1); }
      else if (ev.key === 'ArrowLeft') { ev.preventDefault(); goReview(-1); }
      else if (ev.key === 'c' || ev.key === 'C') { ev.preventDefault(); void reviewCurrent('confirmed'); }
      else if (ev.key === 'r' || ev.key === 'R') { ev.preventDefault(); void reviewCurrent('rejected'); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [reviewQueue, goReview, reviewCurrent]);

  const primaryChannel = channels[0];
  const selInput = "h-9 rounded-md border border-input bg-background px-2 text-sm";

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">YouTube Timeline Miner</h1>
          <p className="text-sm text-muted-foreground">
            {events.length.toLocaleString()} events · {videos.length} videos · {channels.length} channel(s)
            {' · '}
            <span className="text-emerald-500">{counts.confirmed} confirmed</span>{', '}
            <span className="text-red-400">{counts.rejected} rejected</span>{', '}
            <span>{counts.unreviewed} unreviewed</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {email ? <Badge variant="secondary">{email}</Badge> : null}
          <Button size="sm" variant="outline" onClick={() => void reloadAll()} disabled={!!busy}>Refresh</Button>
        </div>
      </div>

      {!db ? (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-3 text-sm text-amber-800 dark:text-amber-200">
          <p className="mb-2 font-medium">Firestore is off in this browser (a leftover debug toggle) — no data can load.</p>
          <Button size="sm" onClick={() => { try { localStorage.removeItem('disableFirestore'); } catch { /* ignore */ } window.location.reload(); }}>
            Re-enable Firestore &amp; reload
          </Button>
        </div>
      ) : null}

      {status ? (
        <div className={`rounded-md border px-3 py-2 text-sm ${status.kind === 'ok'
          ? 'border-green-500/40 bg-green-500/10 text-green-700 dark:text-green-300'
          : 'border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300'}`}>
          {status.msg}
        </div>
      ) : null}

      {/* ---- Channel controls / tools ---- */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Channels &amp; mining tools</CardTitle>
          <CardDescription>Register a channel, backfill uploads, and run AI extraction.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-1">
              <Label htmlFor="channel-input">Add channel</Label>
              <Input id="channel-input" placeholder="@StefanBurns · https://youtube.com/@handle · UC…"
                value={channelInput} onChange={(e) => setChannelInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void register(); }} />
            </div>
            <Button onClick={register} disabled={busy === 'register'}>{busy === 'register' ? 'Registering…' : 'Register + backfill'}</Button>
          </div>
          {channels.some((c) => c.status === 'hidden') ? (
            <button
              type="button"
              onClick={() => setShowHidden((s) => !s)}
              className="self-start text-xs text-muted-foreground underline-offset-2 hover:underline"
            >
              {showHidden ? 'Hide' : 'Show'} {channels.filter((c) => c.status === 'hidden').length} hidden channel(s)
            </button>
          ) : null}
          {(showHidden ? channels : channels.filter((c) => c.status !== 'hidden')).map((c) => {
            const hidden = c.status === 'hidden';
            return (
            <div key={c.channelId} className={`flex flex-wrap items-center gap-2 rounded-md border border-border/50 bg-card/40 px-3 py-2 text-sm ${hidden ? 'opacity-60' : ''}`}>
              <label className="flex items-center gap-1" title="Event colour on the calendar for this channel">
                <input
                  type="color"
                  value={c.color || '#f59e0b'}
                  onChange={(e) => void setChannelColor(c, e.target.value)}
                  className="h-6 w-8 cursor-pointer rounded border border-border/50 bg-transparent p-0"
                />
              </label>
              <span className="font-medium">{c.title ?? c.channelId}</span>
              <Badge variant="outline">{c.videoCount ?? 0} videos</Badge>
              <Badge variant={hidden || c.status !== 'active' ? 'secondary' : 'default'}>{c.status ?? 'unknown'}</Badge>
              <a href={`/channel/${c.channelId}`} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline-offset-2 hover:underline">Profile ↗</a>
              <div className="ml-auto flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => backfill(c)} disabled={busy === `backfill:${c.channelId}`}>
                  {busy === `backfill:${c.channelId}` ? 'Backfilling…' : 'Backfill uploads'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => extract(c, 5)} disabled={busy === `extract:${c.channelId}`}>
                  {busy === `extract:${c.channelId}` ? 'Extracting…' : 'Extract 5 pending'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => extract(c, 25)} disabled={busy === `extract:${c.channelId}`}>
                  Extract 25
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setChannelHidden(c, !hidden)} disabled={busy === `hide:${c.channelId}`}>
                  {busy === `hide:${c.channelId}` ? '…' : hidden ? 'Unhide' : 'Hide'}
                </Button>
              </div>
            </div>
            );
          })}
        </CardContent>
      </Card>

      {/* ---- Tabs ---- */}
      <div className="flex gap-1 border-b border-border/40">
        {(['events', 'videos', 'ask', 'billing'] as const).map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${tab === t ? 'border-b-2 border-primary text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            {t === 'events' ? `Events (${events.length})` : t === 'videos' ? `Videos (${videos.length})` : t === 'ask' ? '✨ Ask AI' : '💳 Usage & Keys'}
          </button>
        ))}
      </div>

      {tab === 'events' ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Timeline events</CardTitle>
            <CardDescription>Click a row to see the transcript quote, topics, and jump to the source moment.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* filters */}
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[240px] space-y-1">
                <Label>Search</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder={aiMode ? 'ask anything — AI semantic search…' : 'title, summary, topic, quote…'}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => { if (aiMode && e.key === 'Enter') void runAiSearch(); }}
                  />
                  {aiMode ? (
                    <Button onClick={() => void runAiSearch()} disabled={aiBusy}>{aiBusy ? 'Searching…' : 'AI Search'}</Button>
                  ) : null}
                </div>
                <div className="flex items-center gap-2 pt-1 text-xs">
                  <button type="button" onClick={() => { setAiMode(false); setAiResults(null); }}
                    className={!aiMode ? 'font-semibold text-foreground' : 'text-muted-foreground hover:text-foreground'}>Keyword</button>
                  <span className="text-muted-foreground">·</span>
                  <button type="button" onClick={() => setAiMode(true)}
                    className={aiMode ? 'font-semibold text-primary' : 'text-muted-foreground hover:text-foreground'}>AI semantic</button>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Review</Label>
                <select className={selInput} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">All</option>
                  <option value="unreviewed">Unreviewed ({counts.unreviewed})</option>
                  <option value="confirmed">Confirmed ({counts.confirmed})</option>
                  <option value="rejected">Hidden ({counts.rejected})</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label>Precision</Label>
                <select className={selInput} value={precision} onChange={(e) => setPrecision(e.target.value)}>
                  <option value="all">Any</option>
                  <option value="day">Day</option>
                  <option value="month">Month</option>
                  <option value="year">Year</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label>Min conf.</Label>
                <select className={selInput} value={String(minConf)} onChange={(e) => setMinConf(Number(e.target.value))}>
                  <option value="0">Any</option>
                  <option value="0.6">≥ 0.60</option>
                  <option value="0.7">≥ 0.70</option>
                  <option value="0.85">≥ 0.85</option>
                  <option value="0.9">≥ 0.90</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label>Sort</Label>
                <select className={selInput} value={`${sortBy}:${sortDir}`} onChange={(e) => {
                  const [b, d] = e.target.value.split(':'); setSortBy(b as typeof sortBy); setSortDir(d as typeof sortDir);
                }}>
                  <option value="extractedAt:desc">Newest extracted</option>
                  <option value="extractedAt:asc">Oldest extracted</option>
                  <option value="eventDate:desc">Event date ↓</option>
                  <option value="eventDate:asc">Event date ↑</option>
                  <option value="confidence:desc">Confidence ↓</option>
                  <option value="confidence:asc">Confidence ↑</option>
                </select>
              </div>
              {(search || statusFilter !== 'all' || precision !== 'all' || minConf > 0) ? (
                <Button size="sm" variant="ghost" onClick={() => { setSearch(''); setStatusFilter('all'); setPrecision('all'); setMinConf(0); }}>Clear</Button>
              ) : null}
            </div>

            {/* bulk bar */}
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span>{aiMode
                ? `${(aiResults ?? []).length} AI result${(aiResults ?? []).length === 1 ? '' : 's'}${search.trim() ? ` for “${search.trim()}”` : ' — type a query and hit AI Search'}`
                : `Showing ${shown.length} of ${filtered.length} filtered (${events.length} total)`}</span>
              <button type="button" className="underline hover:text-foreground" onClick={selectAllShown}>
                {allShownSelected ? 'Deselect shown' : 'Select shown'}
              </button>
              {selected.size > 0 ? (
                <>
                  <span>· {selected.size} selected</span>
                  <Button size="sm" variant="outline" disabled={!!busy} onClick={() => bulkReview([...selected], 'confirmed')}>Confirm selected</Button>
                  <Button size="sm" variant="outline" disabled={!!busy} onClick={() => bulkReview([...selected], 'rejected')}>Hide selected</Button>
                  <button type="button" className="underline hover:text-foreground" onClick={() => setSelected(new Set())}>clear</button>
                </>
              ) : null}
            </div>

            {/* table */}
            <div className="overflow-x-auto rounded-md border border-border/40">
              <table className="w-full text-sm">
                <thead className="bg-card/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="w-8 px-2 py-2"></th>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Event</th>
                    <th className="px-3 py-2">Conf.</th>
                    <th className="px-3 py-2">Review</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e) => {
                    const open = expanded === e.id;
                    return (
                      <Fragment key={e.id}>
                        <tr className="border-t border-border/30 align-top hover:bg-card/30">
                          <td className="px-2 py-2">
                            <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggleSel(e.id)} />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="font-medium">{e.eventDateKey ?? '—'}</div>
                            <div className="text-[10px] uppercase text-muted-foreground">{e.datePrecision ?? ''}</div>
                          </td>
                          <td className="px-3 py-2">
                            <button type="button" className="text-left font-medium hover:underline" onClick={() => setExpanded(open ? null : e.id)}>
                              {e.title ?? '(untitled)'}
                            </button>
                            <div className="text-xs text-muted-foreground">{e.channelTitle}</div>
                          </td>
                          <td className="px-3 py-2"><Badge variant={confVariant(e.confidence)}>{(e.confidence ?? 0).toFixed(2)}</Badge></td>
                          <td className="px-3 py-2">
                            <Badge variant={e.reviewStatus === 'confirmed' ? 'default' : e.reviewStatus === 'rejected' ? 'destructive' : 'outline'}>
                              {e.reviewStatus ?? 'unreviewed'}
                            </Badge>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="outline" disabled={!!busy} onClick={() => review(e.id, 'confirmed')}>Confirm</Button>
                              <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => review(e.id, 'rejected')}>Hide</Button>
                            </div>
                          </td>
                        </tr>
                        {open ? (
                          <tr className="border-t border-border/20 bg-card/20">
                            <td></td>
                            <td colSpan={5} className="px-3 py-3">
                              {e.summary ? <p className="mb-2 text-sm">{e.summary}</p> : null}
                              {e.sourceQuote ? (
                                <blockquote className="mb-2 border-l-2 border-primary/50 pl-3 text-sm italic text-muted-foreground">
                                  “{e.sourceQuote}”
                                </blockquote>
                              ) : null}
                              {e.topics && e.topics.length ? (
                                <div className="mb-2 flex flex-wrap gap-1">
                                  {e.topics.map((t) => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}
                                </div>
                              ) : null}
                              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                                {e.videoId ? (
                                  <button type="button"
                                    onClick={() => openReview(e.id)}
                                    className="font-medium text-sky-500 hover:underline">
                                    ▶ Play &amp; review at {typeof e.sourceTimestampSec === 'number' ? fmtDur(e.sourceTimestampSec) : 'start'}
                                  </button>
                                ) : null}
                                {e.model ? <span>model: {e.model}</span> : null}
                                {e.videoId ? <span>video: {e.videoId}</span> : null}
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                  {rows.length === 0 ? (
                    <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                      {aiMode ? (aiBusy ? 'Searching…' : 'No AI results yet — type a query and hit AI Search.') : 'No events match these filters.'}
                    </td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            {!aiMode && filtered.length > shown.length ? (
              <div className="flex justify-center">
                <Button variant="outline" onClick={() => setVisibleCount((n) => n + 100)}>
                  Load more ({filtered.length - shown.length} remaining)
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : tab === 'videos' ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Mined videos</CardTitle>
            <CardDescription>Transcript &amp; extraction status per video. Click a title to open on YouTube.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto rounded-md border border-border/40">
              <table className="w-full text-sm">
                <thead className="bg-card/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Published</th>
                    <th className="px-3 py-2">Title</th>
                    <th className="px-3 py-2">Dur.</th>
                    <th className="px-3 py-2">Views</th>
                    <th className="px-3 py-2">Transcript</th>
                    <th className="px-3 py-2">Extraction</th>
                    <th className="px-3 py-2 text-right">Events</th>
                  </tr>
                </thead>
                <tbody>
                  {videos.map((v) => (
                    <tr key={v.videoId} className="border-t border-border/30 hover:bg-card/30">
                      <td className="px-3 py-2 whitespace-nowrap">{v.publishDateKey ?? (v.publishedAt ?? '').slice(0, 10)}</td>
                      <td className="px-3 py-2 max-w-[420px]">
                        <button type="button" className="text-left font-medium hover:underline"
                          onClick={() => setVideoPlayer({ videoId: v.videoId, title: v.title })}>
                          {v.title ?? v.videoId}
                        </button>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDur(v.durationSec)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{fmtNum(v.viewCount)}</td>
                      <td className="px-3 py-2">
                        <Badge variant={v.transcriptStatus === 'fetched' ? 'default' : v.transcriptStatus === 'none' ? 'destructive' : 'secondary'}>
                          {v.transcriptStatus ?? '—'}{v.transcriptLang ? ` · ${v.transcriptLang}` : ''}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant={v.extractionStatus === 'done' ? 'default' : v.extractionStatus === 'pending' ? 'outline' : 'secondary'}>
                          {v.extractionStatus ?? '—'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right">{v.eventCount ?? 0}</td>
                    </tr>
                  ))}
                  {videos.length === 0 ? (
                    <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">No videos mined yet.</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : tab === 'ask' ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Ask the timeline (AI)</CardTitle>
            <CardDescription>Ask a question; the assistant answers grounded in the mined events and cites them. Click a citation to play &amp; review the source.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="flex-1 space-y-1">
                <Label htmlFor="ask-input">Question</Label>
                <Input id="ask-input" placeholder="e.g. What major earthquakes were mentioned in 2023?"
                  value={askQuestion} onChange={(e) => setAskQuestion(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void runAsk(); }} />
              </div>
              <Button onClick={() => void runAsk()} disabled={askBusy || !askQuestion.trim()}>{askBusy ? 'Thinking…' : 'Ask'}</Button>
            </div>

            {askAnswer ? (
              <div className="rounded-md border border-border/50 bg-card/40 p-4">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Answer</div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{askAnswer}</p>
              </div>
            ) : null}

            {askCitations.length ? (
              <div className="space-y-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cited events ({askCitations.length})</div>
                {askCitations.map((c) => (
                  <div key={c.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border/40 bg-card/30 px-3 py-2 text-sm">
                    <span className="text-xs text-muted-foreground">[{c.n}]</span>
                    <span className="font-medium">{c.title}</span>
                    <Badge variant="outline">{c.eventDateKey}</Badge>
                    <Badge variant={confVariant(c.confidence)}>{(c.confidence ?? 0).toFixed(2)}</Badge>
                    <span className="text-muted-foreground">{c.channelTitle}</span>
                    {c.videoId ? (
                      <Button size="sm" variant="outline" className="ml-auto" onClick={() => openReview(c.id, askCitations)}>▶ Play &amp; review</Button>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}

            {!askAnswer && !askBusy ? (
              <p className="text-xs text-muted-foreground">Powered by embeddings retrieval + Gemini over your {events.length} mined events. External agents can query the same via the <code>askTimeline</code> / <code>searchTimeline</code> endpoints with an API key.</p>
            ) : null}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">AI usage &amp; per-tenant API keys</CardTitle>
            <CardDescription>Issue a key per tenant so their AI usage is metered and billed to them. Totals below feed AppSiteTM chargeback.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Issue a tenant key</div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="tenant-input">Tenant</Label>
                  <Input id="tenant-input" placeholder="e.g. acme-corp" value={newTenant} onChange={(e) => setNewTenant(e.target.value)} />
                </div>
                <div className="flex-1 space-y-1">
                  <Label htmlFor="label-input">Label (optional)</Label>
                  <Input id="label-input" placeholder="e.g. production agent" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
                </div>
                <Button onClick={() => void createKey()} disabled={busy === 'createkey' || !newTenant.trim()}>{busy === 'createkey' ? 'Creating…' : 'Create key'}</Button>
              </div>
              {createdKey ? (
                <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
                  <div className="mb-1">Key for <b>{createdKey.tenant}</b> — copy now, it won’t be shown again:</div>
                  <code className="block break-all rounded bg-black/30 px-2 py-1 text-xs text-amber-100">{createdKey.key}</code>
                </div>
              ) : null}
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Keys</div>
                <Button size="sm" variant="outline" onClick={() => void loadBilling()}>Refresh</Button>
              </div>
              <div className="overflow-x-auto rounded-md border border-border/40">
                <table className="w-full text-sm">
                  <thead className="bg-card/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr><th className="px-3 py-2">Tenant</th><th className="px-3 py-2">Label</th><th className="px-3 py-2">Key id</th><th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">Actions</th></tr>
                  </thead>
                  <tbody>
                    {keys.map((k) => (
                      <tr key={k.keyId} className="border-t border-border/30">
                        <td className="px-3 py-2 font-medium">{k.tenant}</td>
                        <td className="px-3 py-2 text-muted-foreground">{k.label || '—'}</td>
                        <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">{k.keyId.slice(0, 12)}…</td>
                        <td className="px-3 py-2"><Badge variant={k.active ? 'default' : 'secondary'}>{k.active ? 'active' : 'revoked'}</Badge></td>
                        <td className="px-3 py-2 text-right">{k.active ? <Button size="sm" variant="ghost" disabled={busy === `revoke:${k.keyId}`} onClick={() => void revokeKey(k.keyId)}>Revoke</Button> : null}</td>
                      </tr>
                    ))}
                    {keys.length === 0 ? <tr><td colSpan={5} className="px-3 py-4 text-center text-muted-foreground">No tenant keys yet.</td></tr> : null}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Usage</div>
                {usage ? <div className="text-sm">Total: <b>${(usage.totalCostUsd ?? 0).toFixed(4)}</b> · {(usage.totalTokens ?? 0).toLocaleString()} tokens</div> : null}
              </div>
              <div className="overflow-x-auto rounded-md border border-border/40">
                <table className="w-full text-sm">
                  <thead className="bg-card/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr><th className="px-3 py-2">Tenant</th><th className="px-3 py-2">Month</th><th className="px-3 py-2 text-right">Calls</th><th className="px-3 py-2 text-right">Tokens</th><th className="px-3 py-2 text-right">Cost</th></tr>
                  </thead>
                  <tbody>
                    {(usage?.rollups || []).map((r, i) => (
                      <tr key={`${r.tenant}-${r.month}-${i}`} className="border-t border-border/30">
                        <td className="px-3 py-2 font-medium">{r.tenant}</td>
                        <td className="px-3 py-2 text-muted-foreground">{r.month}</td>
                        <td className="px-3 py-2 text-right">{r.calls ?? 0}</td>
                        <td className="px-3 py-2 text-right">{(r.totalTokens ?? 0).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right">${(r.costUsd ?? 0).toFixed(4)}</td>
                      </tr>
                    ))}
                    {(!usage || usage.rollups.length === 0) ? <tr><td colSpan={5} className="px-3 py-4 text-center text-muted-foreground">No usage recorded yet.</td></tr> : null}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-muted-foreground">Chargeback source (aiUsageReport). Prices editable in functions/src/youtube/lib/usage.ts.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Full review station: play source at the event timestamp + review + navigate, in-app. */}
      <Dialog open={!!reviewQueue} onOpenChange={(o) => { if (!o) setReviewQueue(null); }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <span className="truncate">{reviewEvent?.title ?? 'Review'}</span>
              {reviewLen ? <span className="shrink-0 text-xs font-normal text-muted-foreground">{reviewPos + 1} of {reviewLen}</span> : null}
            </DialogTitle>
          </DialogHeader>
          {reviewEvent ? (
            <div className="space-y-3">
              <div className="aspect-video w-full overflow-hidden rounded-md bg-black">
                {reviewEvent.videoId ? (
                  <iframe
                    key={`${reviewEvent.videoId}:${Math.floor(reviewEvent.sourceTimestampSec ?? 0)}`}
                    className="h-full w-full"
                    src={`https://www.youtube-nocookie.com/embed/${reviewEvent.videoId}?start=${Math.floor(reviewEvent.sourceTimestampSec ?? 0)}&autoplay=1&rel=0`}
                    title={reviewEvent.title ?? 'Source video'}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No source video</div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Badge variant="outline">{reviewEvent.eventDateKey ?? '—'}{reviewEvent.datePrecision ? ` · ${reviewEvent.datePrecision}` : ''}</Badge>
                <Badge variant={confVariant(reviewEvent.confidence)}>{(reviewEvent.confidence ?? 0).toFixed(2)}</Badge>
                <Badge variant={reviewEvent.reviewStatus === 'confirmed' ? 'default' : reviewEvent.reviewStatus === 'rejected' ? 'destructive' : 'outline'}>
                  {reviewEvent.reviewStatus ?? 'unreviewed'}
                </Badge>
                <span className="text-muted-foreground">{reviewEvent.channelTitle}</span>
              </div>
              {reviewEvent.summary ? <p className="text-sm">{reviewEvent.summary}</p> : null}
              {reviewEvent.sourceQuote ? (
                <blockquote className="border-l-2 border-primary/50 pl-3 text-sm italic text-muted-foreground">“{reviewEvent.sourceQuote}”</blockquote>
              ) : null}
              {reviewEvent.topics?.length ? (
                <div className="flex flex-wrap gap-1">{reviewEvent.topics.map((t) => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}</div>
              ) : null}
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/40 pt-3">
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => goReview(-1)} disabled={reviewPos <= 0}>← Prev</Button>
                  <Button size="sm" variant="outline" onClick={() => goReview(1)} disabled={reviewPos >= reviewLen - 1}>Next →</Button>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => void reviewCurrent('confirmed')}>Confirm (C)</Button>
                  <Button size="sm" variant="destructive" onClick={() => void reviewCurrent('rejected')}>Hide (R)</Button>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                ← / → navigate · C confirm · R reject · Esc close · Confirm/Reject auto-advances.
              </p>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Plain video playback (Videos tab). */}
      <Dialog open={!!videoPlayer} onOpenChange={(o) => { if (!o) setVideoPlayer(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-base">{videoPlayer?.title ?? 'Video'}</DialogTitle>
          </DialogHeader>
          {videoPlayer ? (
            <div className="aspect-video w-full overflow-hidden rounded-md bg-black">
              <iframe
                key={videoPlayer.videoId}
                className="h-full w-full"
                src={`https://www.youtube-nocookie.com/embed/${videoPlayer.videoId}?autoplay=1&rel=0`}
                title={videoPlayer.title ?? 'Video'}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
